import React, { useState, useEffect } from 'react';
import { Search, Edit2, Trash2, ArrowLeft, Bell, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { fetchMessages, createMessage, updateMessage, deleteMessage } from '../store/quickMessageSlice';
import { useConfirm } from '../context/ConfirmContext';

const getAdminId = () => {
  return Cookies.get('adminId');
};

const QuickMessagesMaster = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  
  const { messages, loading } = useSelector((state) => state.quickMessage);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMessage, setCurrentMessage] = useState('');
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(fetchMessages(getAdminId()));
  }, [dispatch]);

  const handleSave = async () => {
    if (!currentMessage.trim()) return;
    const adminId = getAdminId();
    
    if (editingId) {
      await dispatch(updateMessage({ id: editingId, text: currentMessage }));
    } else {
      await dispatch(createMessage({ adminId, text: currentMessage }));
    }
    
    setCurrentMessage('');
    setEditingId(null);
  };

  const handleEdit = (msg) => {
    setEditingId(msg._id);
    setCurrentMessage(msg.text);
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirm('Are you sure you want to delete this message template?');
    if (!isConfirmed) return;
    await dispatch(deleteMessage(id));
  };

  const handleCancel = () => {
    setCurrentMessage('');
    setEditingId(null);
  };

  const filteredMessages = (messages || []).filter(msg => 
    msg.text?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ padding: '24px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      
      {/* Header section */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, gap: 16 }}>
        <button 
          onClick={() => navigate('/masters')}
          style={{ 
            background: 'var(--input-bg)', 
            border: '1px solid var(--border-light)', 
            color: 'var(--text-primary)',
            padding: '8px 16px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          <ArrowLeft size={16} />
          All Masters
        </button>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>Quick Messages Master</h1>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>Manage alert templates for sending quick messages to monitored workstations</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, flex: 1, flexWrap: 'wrap' }}>
        {/* Left Pane - Form */}
        <div className="dash-glass-card" style={{ flex: '1 1 350px', maxWidth: '100%', padding: 24, display: 'flex', flexDirection: 'column', gap: 24, height: 'fit-content' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
            {editingId ? 'Edit Template Message' : 'Create Template Message'}
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Message Content *</label>
            <div style={{
              background: 'var(--input-bg)',
              border: '1px solid var(--border-light)',
              borderRadius: 8,
              padding: 16,
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start'
            }}>
              <MessageSquare size={16} color="var(--accent-primary)" style={{ marginTop: 2 }} />
              <textarea 
                placeholder="e.g. Please return to your seat immediately."
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: 'var(--text-primary)', 
                  fontSize: 14, 
                  width: '100%',
                  resize: 'none',
                  minHeight: 80,
                  outline: 'none',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 'auto' }}>
            <button 
              onClick={handleCancel}
              style={{
                flex: 1,
                padding: '10px',
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                color: 'var(--text-primary)',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className="btn-primary"
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: 8,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {editingId ? 'Update Message' : 'Save Message'}
            </button>
          </div>
        </div>

        {/* Right Pane - List */}
        <div className="dash-glass-card" style={{ flex: '2 1 400px', minWidth: 0, padding: 24, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 'fit-content', maxHeight: 'calc(100vh - 160px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: 'var(--input-bg)', 
              border: '1px solid var(--border-light)', 
              borderRadius: 8, 
              padding: '8px 16px',
              width: '100%',
              maxWidth: 300,
              gap: 12
            }}>
              <Search size={16} color="var(--text-muted)" />
              <input 
                type="text" 
                placeholder="Search messages..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: 'var(--text-primary)',
                  outline: 'none',
                  width: '100%'
                }}
              />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Active Templates: <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>{messages?.length || 0}</span>
            </div>
          </div>

          <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, paddingRight: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)', width: 80 }}>Index</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>Message Alert Template Text</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)', width: 100, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>Loading...</td>
                  </tr>
                ) : filteredMessages.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>No templates found.</td>
                  </tr>
                ) : (
                  filteredMessages.map((msg, index) => (
                    <tr key={msg._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 13 }}>#{index + 1}</td>
                      <td style={{ padding: '16px', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Bell size={14} color="var(--accent-primary)" />
                          {msg.text}
                        </div>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button 
                            onClick={() => handleEdit(msg)}
                            style={{ 
                              background: 'var(--input-bg)', 
                              border: '1px solid var(--border-light)', 
                              padding: '6px', 
                              borderRadius: 4, 
                              color: 'var(--text-primary)',
                              cursor: 'pointer' 
                            }}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button 
                            onClick={() => handleDelete(msg._id)}
                            style={{ 
                              background: 'rgba(239, 68, 68, 0.1)', 
                              border: '1px solid rgba(239, 68, 68, 0.3)', 
                              padding: '6px', 
                              borderRadius: 4, 
                              color: '#EF4444',
                              cursor: 'pointer' 
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickMessagesMaster;
